import { defineStore } from 'pinia'
import { getContentReviewProfiles } from './api'
import type { ReviewCriteriaProfile, CriteriaItem } from './api'

function matchesRelease(p: ReviewCriteriaProfile, betaMode: boolean): boolean {
  return betaMode ? p.released || p.beta_only : p.released && !p.beta_only
}

function findProfileCriteria(
  profiles: ReviewCriteriaProfile[],
  profileId: string,
  contentType: string,
  betaMode: boolean
): CriteriaItem[] {
  const normalizedId = profileId.toLowerCase()

  let profile = profiles.find(
    (p) => p.content_profile.toLowerCase() === normalizedId && matchesRelease(p, betaMode)
  )

  if (!profile) {
    profile = profiles.find((p) => p.content_profile === '' && matchesRelease(p, betaMode))
  }

  return profile?.criteria_by_type[contentType] ?? []
}

export const useContentReviewStore = defineStore('contentReview', {
  state: () => ({
    profiles: [] as ReviewCriteriaProfile[],
    loading: false,
    error: null as string | null,
  }),

  actions: {
    async fetchProfiles() {
      if (this.loading) return
      this.loading = true
      this.error = null
      try {
        this.profiles = await getContentReviewProfiles()
      } catch (err) {
        console.error('Failed to fetch content review profiles:', err)
        this.error = 'Failed to load review criteria'
        this.profiles = []
      } finally {
        this.loading = false
      }
    },
  },

  getters: {
    criteriaForProfile(state) {
      return (profileId: string, contentType: string, betaMode: boolean): CriteriaItem[] =>
        findProfileCriteria(state.profiles, profileId, contentType, betaMode)
    },
    impactCriteriaForProfile(state) {
      return (profileId: string, betaMode: boolean): CriteriaItem[] =>
        findProfileCriteria(state.profiles, profileId, 'impact', betaMode)
    },
    criteriaSetName(state) {
      return (profileId: string, betaMode: boolean): string => {
        const normalizedId = profileId.toLowerCase()
        const profile = state.profiles.find(
          (p) => p.content_profile.toLowerCase() === normalizedId && matchesRelease(p, betaMode)
        )
        return profile?.criteria_set_name ?? ''
      }
    },
    isCustomCriteriaSet(state) {
      return (profileId: string, betaMode: boolean): boolean => {
        if (!profileId) return false
        const normalizedId = profileId.toLowerCase()
        return state.profiles.some(
          (p) =>
            p.content_profile !== '' &&
            p.content_profile.toLowerCase() === normalizedId &&
            matchesRelease(p, betaMode)
        )
      }
    },
  },
})
